/* eslint-disable max-statements */
import React, { useState, useEffect } from 'react'
import { useLocation } from '@reach/router'
import {
  organicFlowRoutesList,
  landingRoutes,
  applicationCompleteRoutes,
} from '../constants/routes'
import { productTypes } from '../constants'
import { indexOf } from 'lodash-es'
import serverData from 'ppbl/utils/server-data'
import { clearSessionStorageExceptCampaign } from '../../__utils__'
import clientLogger from 'ppbl/utils/client-logger'

type Props = {
  path: string
  applicationType: string
}

const withRouteGuard = function (
  Component: React.ComponentType<Props>,
  Loader: React.ComponentType,
) {
  return function WrappedComponent(props: Props) {
    const [loading, setLoading] = useState(true)
    const location = useLocation()

    const checkTransition = () => {
      const activeApplicationPage = location.pathname
        .split('/')
        .pop()
        .split('?')[0]

      const mostRecentCompletedPage = sessionStorage.getItem(
        'mostRecentCompletedPage',
      )
      const { requestURI } = serverData

      const product = serverData.startParams?.product
      const country = serverData.startParams?.country
      const applicationMaintenance =
        serverData.data.appConfig.application.applicationMaintenance
      const isPaypalLogedin = !!serverData?.user

      let isRedirectionValid = true
      let redirectURL = ''

      if (applicationMaintenance) {
        isRedirectionValid = false
        redirectURL = `${requestURI}/maintenance`
        clientLogger.info(
          'Maintenance Flag is turned ON - redirecting to:',
          redirectURL,
        )
        return {
          isRedirectionValid,
          redirectURL,
        }
      }

      if (!productTypes.includes(product)) {
        isRedirectionValid = false
        redirectURL = `${requestURI}/app/start`
        clientLogger.info(
          'Product type not recognized - redirecting to:',
          redirectURL,
        )
        return {
          isRedirectionValid,
          redirectURL,
        }
      }

      if (
        activeApplicationPage === 'applicationPage1' &&
        product === 'paypal' &&
        !isPaypalLogedin
      ) {
        // redirect, if not logged in
        redirectURL = `${requestURI}/login`
        isRedirectionValid = false
        clientLogger.info(
          'PayPal customer not logged in - redirecting to:',
          redirectURL,
        )
        return {
          isRedirectionValid,
          redirectURL,
        }
      }

      const isApplicationCompleted =
        activeApplicationPage === 'loading' ||
        applicationCompleteRoutes.includes(mostRecentCompletedPage)

      if (!isApplicationCompleted) {
        clientLogger.info('Application Status - Not Complete')
        if (
          !organicFlowRoutesList.includes(activeApplicationPage) ||
          (!mostRecentCompletedPage &&
            !landingRoutes.includes(activeApplicationPage)) ||
          Number(activeApplicationPage.slice(-1)) -
            Number(
              !!mostRecentCompletedPage && mostRecentCompletedPage.slice(-1),
            ) >
            1
        ) {
          // Send the user back to page 1 if they skipped some of the application pages.
          isRedirectionValid = false
          clearSessionStorageExceptCampaign()

          redirectURL = `${requestURI}/${country}/${product}/start`
          clientLogger.info(
            `Illegal routing transaction - 
              Route not present in known list_${!organicFlowRoutesList.includes(
                activeApplicationPage,
              )}
              User flow is not through start route_${
                !mostRecentCompletedPage &&
                !landingRoutes.includes(activeApplicationPage)
              }
               - Redirection URL set to:`,
            redirectURL,
          )
        }
      } else if (isApplicationCompleted) {
        clientLogger.info('Application Status - Complete')
        const activePageIdx = indexOf(
          organicFlowRoutesList,
          activeApplicationPage,
          0,
        )
        const recentCompletedPageIdx = indexOf(
          organicFlowRoutesList,
          mostRecentCompletedPage,
          0,
        )
        const handleApplicationCompleteRedirection = () => {
          if (activePageIdx < recentCompletedPageIdx) {
            isRedirectionValid = false
            clientLogger.info(
              `Illegal routing transaction - ActivePage:${activePageIdx} < RecentCompletedPageIdx:${recentCompletedPageIdx}`,
              redirectURL,
            )
            return `${requestURI}/application/${country}/${product}/${mostRecentCompletedPage}`
          }
          return redirectURL
        }
        redirectURL = handleApplicationCompleteRedirection()
      }

      return {
        isRedirectionValid,
        redirectURL,
      }
    }

    useEffect(() => {
      const { isRedirectionValid, redirectURL } = checkTransition()

      if (isRedirectionValid) {
        setLoading(false)
      } else {
        window.location.assign(redirectURL)
      }
    }, [location])

    return <>{loading ? <Loader /> : <Component {...props} />}</>
  }
}

export default withRouteGuard
